import { useMemo } from 'react';
import { useTourContext } from '@/store/tourContext';
import { isOnboardingTutorial as isOnboardingTutorialUtil } from '@/utils/tutorials/tutorialsUtils';

/**
 * Tutorial name constants for consistency
 */
export const TUTORIAL_NAMES = {
	// Onboarding tutorials
	ONBOARDING_USER: 'OnboardingUser',
	ONBOARDING_WC: 'OnboardingWCTutorial',
	ONBOARDING_SALES_REP: 'OnboardingSalesRepTutorial',
	ONBOARDING_GRAFT_LOGS: 'OnboardingTutorialGraftLogs',
	ONBOARDING_PAYMENT_MSL: 'OnboardingTutorialPaymentMSL',
	ONBOARDING_MINDSIGHT: 'OnboardingMindsightTutorial',
	
	// Invoices tutorials
	INVOICES: 'InvoicesTutorial',
	INVOICES_WC: 'InvoicesTutorialWC',
	INVOICES_SALES_REP: 'InvoicesTutorialSalesRep',
	INVOICES_MINDSIGHT: 'InvoicesTutorialMindsight',
	
	// Returns tutorials
	RETURNS: 'ReturnsTutorial',
	RETURNS_WC: 'ReturnsTutorialWC',
	RETURNS_SALES_REP: 'ReturnsTutorialSalesRep',
	RETURNS_MINDSIGHT: 'ReturnsTutorialMindsight',
	
	// Benefit Verification tutorials
	BV_WC: 'BenefitVerificationTutorialWC',
	BV_SALES_REP: 'BenefitVerificationTutorialSalesRep',
	BV_MINDSIGHT: 'BenefitVerificationTutorialMindsight',
	
	// Requests tutorials
	REQUESTS: 'OrderStatusTutorial',
	REQUESTS_WC: 'OrderStatusTutorialWC',
	REQUESTS_MINDSIGHT: 'OrderStatusTutorialMindsight',
} as const;

/**
 * Helper function to check if a tutorial name is an onboarding tutorial
 * Re-exported from utils for convenience
 */
export const isOnboardingTutorial = isOnboardingTutorialUtil;

/**
 * Hook to check if specific tutorial types are active
 * Centralizes all tutorial active checks to avoid duplication
 */
export const useTutorialActive = () => {
	const { isTourActive, activeTutorialName } = useTourContext();

	// Memoize all checks for performance
	const checks = useMemo(() => {
		// Onboarding tutorials
		const isOnboardingActive =
			isTourActive && isOnboardingTutorialUtil(activeTutorialName);

		const isInvoicesTutorialActive =
			isTourActive &&
			(activeTutorialName === TUTORIAL_NAMES.INVOICES ||
				activeTutorialName === TUTORIAL_NAMES.INVOICES_WC ||
				activeTutorialName === TUTORIAL_NAMES.INVOICES_SALES_REP ||
				activeTutorialName === TUTORIAL_NAMES.INVOICES_MINDSIGHT);

		const isInvoicesSalesRepTutorialActive =
			isTourActive &&
			activeTutorialName === TUTORIAL_NAMES.INVOICES_SALES_REP;

		// Check if any invoice or onboarding tutorial is active
		// This includes all invoice tutorials and all onboarding tutorials
		const isAnyInvoiceOrOnboardingTutorialActive =
			isTourActive &&
			(isInvoicesTutorialActive || isOnboardingActive);

		const isReturnsTutorialActive =
			isTourActive &&
			(activeTutorialName === TUTORIAL_NAMES.RETURNS ||
				activeTutorialName === TUTORIAL_NAMES.RETURNS_WC ||
				activeTutorialName === TUTORIAL_NAMES.RETURNS_SALES_REP ||
				activeTutorialName === TUTORIAL_NAMES.RETURNS_MINDSIGHT);

		// Benefit Verification tutorials (includes onboarding)
		const isBVTutorialActive =
			isTourActive &&
			(activeTutorialName === TUTORIAL_NAMES.BV_WC ||
				activeTutorialName === TUTORIAL_NAMES.BV_SALES_REP ||
				activeTutorialName === TUTORIAL_NAMES.BV_MINDSIGHT ||
				isOnboardingTutorialUtil(activeTutorialName));

		const isRequestsTutorialActive =
			isTourActive &&
			(activeTutorialName === TUTORIAL_NAMES.REQUESTS ||
				activeTutorialName === TUTORIAL_NAMES.REQUESTS_WC ||
				activeTutorialName === TUTORIAL_NAMES.REQUESTS_MINDSIGHT ||
				(isOnboardingActive &&
					(activeTutorialName === TUTORIAL_NAMES.ONBOARDING_MINDSIGHT ||
						activeTutorialName === TUTORIAL_NAMES.ONBOARDING_WC ||
						activeTutorialName === TUTORIAL_NAMES.ONBOARDING_SALES_REP)));

		return {
			isOnboardingActive,
			isInvoicesTutorialActive,
			isInvoicesSalesRepTutorialActive,
			isAnyInvoiceOrOnboardingTutorialActive,
			isReturnsTutorialActive,
			isBVTutorialActive,
			isRequestsTutorialActive,
		};
	}, [isTourActive, activeTutorialName]);

	return checks;
};

